// One small store for the picker: the company's policy (per company) and each
// chat's selection (per thread). The chat's saved row is the truth; a person's
// pick is shown at once, saved shortly after, and carried once by the next
// sent message (captureModelSelection). A brand-new chat also carries the displayed
// company default, before its row exists. A failed send retains the selection
// until the server admits a request. Nothing else is sent: a model the
// server set itself (a Skill run or "switch this chat to …") is never
// overwritten by a stale chip.
import { useEffect, useSyncExternalStore } from 'react';
import {
  fetchModelPolicy,
  fetchThreadSelection,
  resolveSelection,
  saveThreadSelection,
  type Effort,
  type ModelPolicyState,
  type Selection,
} from './model-policy';

type PolicyEntry = { status: 'loading' | 'ready' | 'off'; state: ModelPolicyState | null };
type Saved = { choice: string; effort: string | null } | null;
type ThreadEntry = {
  /** New chats have no row; saved chats stay `loading` until their choice is known. */
  status: 'new' | 'loading' | 'ready';
  saved: Saved;
  /** The person's pick; its effort is the one they asked for, before any clamping. */
  picked: Selection | null;
  /** New-chat default or a pick not yet carried by a sent message. */
  unsent: boolean;
  /** A row read started before admission must not replace the admitted selection. */
  readId?: symbol;
};

const SAVE_DELAY_MS = 300;
const policies = new Map<string, PolicyEntry>();
const threads = new Map<string, ThreadEntry>();
const saves = new Map<string, ReturnType<typeof setTimeout>>();
const listeners = new Set<() => void>();
let version = 0;
let lastOrganization: string | null = null;

const emit = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};
const readVersion = () => version;
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Loads once; `refresh` re-reads while the current state stays on screen. */
export function loadModelPolicy(organizationId: string, refresh = false) {
  const entry = policies.get(organizationId);
  if (entry && !refresh) return;
  if (!entry) policies.set(organizationId, { status: 'loading', state: null });
  void fetchModelPolicy(organizationId).then(
    (state) => {
      policies.set(organizationId, { status: state ? 'ready' : 'off', state });
      emit();
    },
    () => {
      // Unknown, not off: keep what is shown, and try again next time.
      if (policies.get(organizationId)?.status === 'loading') policies.delete(organizationId);
      emit();
    },
  );
}

/** Reads the chat's saved row; after a turn this shows any change the server made. */
export function loadModelThread(
  threadId: string,
  { refresh = false, persisted = true }: { refresh?: boolean; persisted?: boolean } = {},
) {
  const entry = threads.get(threadId);
  if (!persisted) {
    if (!entry) {
      threads.set(threadId, { status: 'new', saved: null, picked: null, unsent: true });
      emit();
    }
    return;
  }
  if (entry && entry.status !== 'new' && !refresh) return;
  const readId = Symbol();
  threads.set(threadId, {
    saved: null,
    picked: null,
    unsent: false,
    ...entry,
    status: entry?.status === 'ready' ? 'ready' : 'loading',
    readId,
  });
  void fetchThreadSelection(threadId).then(
    (saved) => {
      const current = threads.get(threadId);
      if (!current || current.readId !== readId) return;
      threads.set(threadId, {
        status: 'ready',
        saved,
        picked: current.unsent ? current.picked : null,
        unsent: current.unsent,
      });
      emit();
    },
    () => {
      // A failed read is unknown, never "nothing saved": show and send nothing.
      const current = threads.get(threadId);
      if (current?.readId === readId && current.status === 'loading') threads.delete(threadId);
      emit();
    },
  );
}

/** The policy after Settings saved it, so open chats follow at once. */
export function adoptModelPolicy(state: ModelPolicyState) {
  policies.set(state.policy.organizationId, { status: 'ready', state });
  emit();
}

export function chooseModelSelection(threadId: string, picked: Selection) {
  const entry = threads.get(threadId) ?? {
    status: 'loading' as const,
    saved: null,
    picked: null,
    unsent: false,
  };
  threads.set(threadId, { ...entry, picked, unsent: true });
  emit();
  // Arrowing through the list saves only where it stops.
  clearTimeout(saves.get(threadId));
  if (entry.status === 'new') return;
  saves.set(
    threadId,
    setTimeout(() => {
      saves.delete(threadId);
      const selection = modelPickerState(lastOrganization, threadId).selection;
      if (selection) void saveThreadSelection(threadId, selection);
    }, SAVE_DELAY_MS),
  );
}

/**
 * The picker's view of one chat. `selection` is what the next answer uses
 * (null until known); `wantedEffort` is the effort the person asked for, kept
 * while they try models that lack it.
 */
export function modelPickerState(organizationId: string | null, threadId: string | null) {
  const policy = organizationId ? (policies.get(organizationId) ?? null) : null;
  const entry = threadId ? threads.get(threadId) : undefined;
  const wanted = entry?.picked ?? (entry && entry.status !== 'loading' ? entry.saved : undefined);
  if (!policy?.state || wanted === undefined)
    return { policy, selection: null, wantedEffort: null };
  const selection = resolveSelection(policy.state, wanted);
  const wantedEffort = (entry?.picked?.effort ?? selection.effort) as Effort;
  return { policy, selection, wantedEffort };
}

export function useModelPicker(
  organizationId: string | null,
  threadId: string | null,
  persisted: boolean,
) {
  useEffect(() => {
    if (!organizationId) return;
    lastOrganization = organizationId;
    loadModelPolicy(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (threadId && organizationId) loadModelThread(threadId, { persisted });
  }, [threadId, organizationId, persisted]);
  useSyncExternalStore(subscribe, readVersion, readVersion);
  return modelPickerState(organizationId, threadId);
}

/** Capture a pending choice and consume only that pick after request admission. */
export function captureModelSelection(threadId: string) {
  const entry = threads.get(threadId);
  if (!entry?.unsent) return null;
  const { selection } = modelPickerState(lastOrganization, threadId);
  if (!selection) return null;
  // Keep the displayed default stable while the first history mirror creates its row.
  const picked = entry.picked ?? selection;
  threads.set(threadId, { ...entry, picked });
  return {
    body: { modelChoice: selection.choice, effort: selection.effort },
    accepted: () => {
      const current = threads.get(threadId);
      if (!current) return;
      // Object identity is the pick's revision: even reselecting the same model
      // may retain a different wanted effort. An older send cannot consume it.
      const same = current.picked === picked;
      threads.set(threadId, {
        status: 'ready',
        saved: selection,
        picked: same ? null : current.picked,
        unsent: current.unsent && !same,
      });
      emit();
    },
  };
}

/** Test seam. */
export function resetModelPickerStore() {
  saves.forEach((timer) => clearTimeout(timer));
  saves.clear();
  policies.clear();
  threads.clear();
  lastOrganization = null;
  emit();
}

/** Test seam: the company whose policy a sent message reads. */
export function setModelPickerOrganization(organizationId: string | null) {
  lastOrganization = organizationId;
}
